/**
 * VideoPage · /video · Recursion-safe standalone renderer
 *
 * P0 bug fix 2026-07-05: previously the app iframe pointed at `/video` which
 * had no route → fell through the wildcard `*` → re-mounted the full Shell
 * → whose HomePage contained the same iframe → infinite recursion → blank
 * white screen crash.
 *
 * This component:
 *   1. Renders WITHOUT the Cathedral Shell (no sidebar, no HomePage, no
 *      inner iframe). Zero risk of nesting recursion.
 *   2. Is mounted at BOTH the outer router (`/video`) and the inner router
 *      (`/video`) so it wins before any wildcard catch-all can fire.
 *   3. Detects if it's already inside an iframe and renders a minimal
 *      static placeholder instead of any further iframe.
 */
import React from "react";

const FEDERATION_VIDEO_STATIC_URL =
  "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4";

export default function VideoPage() {
  const insideIframe =
    typeof window !== "undefined" && window.self !== window.top;

  return (
    <main
      data-testid="video-page"
      data-inside-iframe={insideIframe ? "true" : "false"}
      style={{
        minHeight:  "100vh",
        background: "#04050a",
        color:      "#e6fbff",
        display:    "flex",
        flexDirection: "column",
        alignItems:  "center",
        justifyContent: "center",
        padding:    "24px 16px",
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: 960,
          width:    "100%",
          background: "rgba(15,23,42,0.85)",
          border:     "1px solid rgba(6,182,212,0.32)",
          borderRadius: 14,
          padding:      "22px 20px",
          boxShadow:    "0 0 22px rgba(6,182,212,0.16)",
        }}
      >
        <div
          className="font-mono"
          style={{
            fontSize:      11,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color:         "#7fe7f5",
            marginBottom:  10,
          }}
        >
          ◉ Federation Video Hub
        </div>
        <h1
          data-testid="video-title"
          style={{
            fontSize:     "clamp(24px, 3.4vw, 36px)",
            lineHeight:   1.1,
            color:        "#e6fbff",
            margin:       "0 0 14px 0",
            letterSpacing: "-0.01em",
          }}
        >
          NextXus Video Broadcast
        </h1>
        <p style={{ color: "rgba(207,234,242,0.7)", lineHeight: 1.55, marginBottom: 18, fontSize: 14 }}>
          Standalone video playback surface. This page runs without the
          Cathedral shell so it can safely be embedded via <code>&lt;iframe&gt;</code>
          without recursion.
        </p>

        <video
          data-testid="video-player"
          controls
          playsInline
          preload="metadata"
          style={{
            width:        "100%",
            maxHeight:    520,
            borderRadius: 10,
            background:   "#000",
            border:       "1px solid rgba(6,182,212,0.24)",
          }}
        >
          <source src={FEDERATION_VIDEO_STATIC_URL} type="video/mp4" />
          Your browser does not support HTML5 video.
        </video>

        <div
          style={{
            marginTop: 14,
            fontSize:  10.5,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color:  "rgba(207,234,242,0.45)",
            fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
          }}
        >
          Federation Video · Recursion-safe · No shell wrap
        </div>
      </div>
    </main>
  );
}
